import { useEffect, useRef, useState } from 'react';
import { fetchMyActivity, type ActivityEvent } from '../socialApi';
import { HopperIcon } from '../ui/icons/HopperIcon';

const POLL_MS = 45_000;
const PAGE_SIZE = 50;

type Celebration = {
  eventId: string;
  familyName: string;
  tierName: string;
  iconUrl: string | null;
};

function stringPayload(event: ActivityEvent, key: string): string | null {
  const value = event.payload[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function toCelebration(event: ActivityEvent): Celebration | null {
  if (event.type !== 'achievement_unlocked') return null;
  return {
    eventId: event.id,
    familyName: stringPayload(event, 'achievement_family_name') ?? 'Новое достижение',
    tierName: stringPayload(event, 'achievement_name') ?? 'Новая ступень',
    iconUrl: stringPayload(event, 'icon_url'),
  };
}

export function AchievementCelebrationWatcher() {
  const latestSeen = useRef<string | null>(null);
  const initialized = useRef(false);
  const polling = useRef(false);
  const [queue, setQueue] = useState<Celebration[]>([]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        const response = await fetchMyActivity(null, PAGE_SIZE);
        if (cancelled) return;
        latestSeen.current = response.data[0]?.id ?? null;
        initialized.current = true;
      } catch {
        initialized.current = false;
      }
    };

    const poll = async () => {
      if (polling.current || cancelled) return;
      if (!initialized.current) {
        await initialize();
        return;
      }

      polling.current = true;
      try {
        const previous = latestSeen.current;
        let response = await fetchMyActivity(null, PAGE_SIZE);
        if (cancelled || response.data.length === 0) return;

        const newestId = response.data[0].id;
        const collected: ActivityEvent[] = [];
        let foundPrevious = previous === null;

        while (true) {
          for (const event of response.data) {
            if (previous !== null && event.id === previous) {
              foundPrevious = true;
              break;
            }
            collected.push(event);
          }
          if (foundPrevious || !response.meta.has_more || !response.meta.next_cursor) break;
          response = await fetchMyActivity(response.meta.next_cursor, PAGE_SIZE);
        }

        if (cancelled) return;
        latestSeen.current = newestId;

        // If the previously seen event has aged out or disappeared, we cannot prove
        // which collected rows are genuinely new. Reset the baseline without replaying
        // historical achievement events as celebrations.
        if (previous !== null && !foundPrevious) return;

        const celebrations = collected.slice().reverse().map(toCelebration).filter((item): item is Celebration => item !== null);
        if (celebrations.length > 0) setQueue((current) => [...current, ...celebrations]);
      } catch {
        // Keep the last known event id and retry on the next interval.
      } finally {
        polling.current = false;
      }
    };

    void initialize();
    const timer = window.setInterval(() => void poll(), POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (queue.length === 0) return undefined;
    const timer = window.setTimeout(() => setQueue((current) => current.slice(1)), 5200);
    return () => window.clearTimeout(timer);
  }, [queue]);

  const current = queue[0] ?? null;
  if (!current) return null;

  return (
    <div className="achievement-celebration" role="status" aria-live="polite" data-event-id={current.eventId}>
      <div className="achievement-celebration__badge">
        {current.iconUrl ? <img src={current.iconUrl} alt="" /> : <HopperIcon name="trophy" size={28} />}
      </div>
      <div><span>Новая ступень</span><strong>{current.familyName} · {current.tierName}</strong></div>
      <HopperIcon name="sparkle" size={19} />
    </div>
  );
}
